/*
 * DID IT REACH THE SERVER? (organiser, 27 Oct 2026: «треба щоб всі зміни, що
 * вносить секретар, якось показувалися, що вони передалися на сервер… це
 * теж стосується, коли секретар вносить результати поєдинку».)
 *
 * Every request that CHANGES something — POST, PUT, PATCH, DELETE — is
 * counted here, on the one HTTP client every screen uses, so no screen can
 * forget to report a save. Reads are not counted; neither is signing in.
 * `SaveStatus` shows the state; anything may subscribe.
 *
 * Nor is a participant's phone turning its bell on or off (participants'
 * monitor, /api/public/monitor/…/push): nobody there is saving anything, and
 * a secretary's «Зберігається…» on a parent's phone only puzzles.
 */
const WRITES = ['post', 'put', 'patch', 'delete'];
// POSTs that write nothing — a login, a token, a phone's subscription, and the
// schedule's duration preview, which the «Розподіл категорій» tab asks for as
// it opens (2 Nov 2026: «чому запис відбувається при переході між табами» —
// it read «Збережено» though nothing was saved).
const QUIET = [/\/api\/authentication_token/, /\/api\/token\/refresh/, /\/api\/public\/monitor\//, /\/api\/schedule\/preview-durations/];

/*
 * WHAT DID NOT GET IN (organiser, 3 Nov 2026: «коли вносяться результати
 * поєдинків, щоб секретар… бачив, що щось не внеслося»). `failed` alone was
 * cleared by the next save that worked — any save, so a point refused at
 * 10:23 was forgotten by a penalty saved at 10:24. Each refused write is kept
 * in `failures` until THE SAME write (method, address and body) succeeds — the
 * secretary entered it again — or the secretary dismisses the list. Never
 * re-sent from here: a «+1» that did arrive, its answer lost, would count twice.
 */
let state = { pending: 0, savedAt: null, failed: false, seen: false, failures: [] };
const listeners = new Set();

const publish = next => {
    state = { ...state, ...next };
    listeners.forEach(listener => listener(state));
};

export const isTrackedWrite = config => Boolean(config)
    && WRITES.includes(String(config.method || '').toLowerCase())
    && !QUIET.some(pattern => pattern.test(String(config.url || '')));

export const saveStarted = config => {
    if (isTrackedWrite(config)) {
        config.__tracked = true;
        publish({ pending: state.pending + 1, seen: true });
    }

    return config;
};

/** One write, whatever its moment — the same action sent again has the same key. */
const keyOf = config => [
    String(config.method || '').toUpperCase(),
    String(config.url || '').replace(/^https?:\/\/[^/]+/, ''),
    'string' === typeof config.data ? config.data : JSON.stringify(config.data || null),
].join(' ');

/** What the server said, or null when nothing came back (no connection). */
const messageOf = error => {
    const response = error && error.response;
    if (!response) {
        return null;
    }
    const data = response.data || {};

    return data.message || data.title || data.detail || data['hydra:description'] || `HTTP ${response.status}`;
};

const MAX_FAILURES = 20;

const finished = (config, ok, error) => {
    if (config && config.__tracked) {
        config.__tracked = false;
        const key = keyOf(config);
        const failures = ok
            ? state.failures.filter(failure => failure.key !== key)
            : [...state.failures.filter(failure => failure.key !== key), { key, at: new Date(), message: messageOf(error) }].slice(-MAX_FAILURES);
        publish({
            pending: Math.max(0, state.pending - 1),
            failures,
            ...(ok ? { savedAt: new Date(), failed: failures.length > 0 } : { failed: true }),
        });
    }
};

/** The secretary has seen them — the list is closed. */
export const dismissFailures = () => publish({ failures: [], failed: false });

export const saveSucceeded = response => {
    finished(response && response.config, true);

    return response;
};

export const saveFailed = error => {
    finished(error && error.config, false, error);

    return Promise.reject(error);
};

export const subscribeSaves = listener => {
    listeners.add(listener);
    listener(state);

    return () => listeners.delete(listener);
};

/** For tests: back to nothing seen. */
export const resetSaves = () => publish({ pending: 0, savedAt: null, failed: false, seen: false, failures: [] });
